import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid, Chip, Stack,
  CircularProgress, Alert, Divider, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField,
} from '@mui/material';
import { ArrowBack, Cancel, CheckCircle } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { paymentRequestService } from '@smartgrow/shared';
import type { PaymentRequestStatus } from '@smartgrow/shared';

const STATUS_COLORS: Record<PaymentRequestStatus, 'default' | 'warning' | 'success' | 'error'> = {
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'error',
  PAID: 'default',
};

const fmt = (n: number) => `₦${n.toLocaleString('en-NG')}`;

const Row = ({ label, value }: { label: string; value?: string | number | null }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight="medium" sx={{ textAlign: 'right', maxWidth: '65%' }}>
      {value ?? '—'}
    </Typography>
  </Box>
);

export const PaymentRequestDetailPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const requestId = id!;
  const [actionError, setActionError] = useState('');
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const userData = JSON.parse(localStorage.getItem('user_data') ?? '{}');
  const isAdminOrOwner = userData?.role === 'ADMIN' || userData?.role === 'OWNER';

  const { data: request, isLoading, isError } = useQuery({
    queryKey: ['payment-request', requestId],
    queryFn: () => paymentRequestService.getById(requestId),
    enabled: !!requestId,
  });

  const approveMutation = useMutation({
    mutationFn: () => paymentRequestService.approve(requestId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-request', requestId] });
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      setActionError('');
    },
    onError: () => setActionError('Failed to approve request.'),
  });

  const rejectMutation = useMutation({
    mutationFn: (reason: string) => paymentRequestService.reject(requestId, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['payment-request', requestId] });
      qc.invalidateQueries({ queryKey: ['payment-requests'] });
      setRejectDialogOpen(false);
      setRejectReason('');
      setActionError('');
    },
    onError: () => setActionError('Failed to reject/cancel request.'),
  });

  if (isLoading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}><CircularProgress /></Box>;
  }

  if (isError || !request) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Failed to load payment request.</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} sx={{ mt: 2 }}>Back</Button>
      </Box>
    );
  }

  const canApprove = isAdminOrOwner && request.status === 'PENDING';
  const canCancel = isAdminOrOwner && (request.status === 'PENDING' || request.status === 'APPROVED');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 } }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold">
            {request.requestType?.replace(/_/g, ' ')}{request.contractorName ? ` — ${request.contractorName}` : ''}
          </Typography>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">{request.requestNumber}</Typography>
        </Box>
        <Chip label={request.status} color={STATUS_COLORS[request.status]} size="small" />
      </Stack>

      {actionError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError('')}>{actionError}</Alert>}

      {(canApprove || canCancel) && (
        <Stack direction="row" spacing={1} sx={{ mb: 3 }}>
          {canApprove && (
            <Button
              variant="contained"
              color="success"
              startIcon={approveMutation.isPending ? <CircularProgress size={16} /> : <CheckCircle />}
              disabled={approveMutation.isPending}
              onClick={() => approveMutation.mutate()}
            >
              Approve
            </Button>
          )}
          {canCancel && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<Cancel />}
              onClick={() => { setRejectDialogOpen(true); setRejectReason(''); }}
            >
              {request.status === 'APPROVED' ? 'Cancel' : 'Reject'}
            </Button>
          )}
        </Stack>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Request Details</Typography>
              <Divider sx={{ mb: 1 }} />
              <Row label="Amount" value={fmt(request.amount)} />
              <Row label="Type" value={request.requestType?.replace(/_/g, ' ')} />
              <Row label="Description" value={request.description} />
              <Row label="Requested By" value={request.requestedByName} />
              <Row label="Request Date" value={request.requestDate ? new Date(request.requestDate).toLocaleDateString() : undefined} />
              {request.notes && <Row label="Notes" value={request.notes} />}
            </CardContent>
          </Card>

          <Card sx={{ mt: 3 }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Approval</Typography>
              <Divider sx={{ mb: 1 }} />
              {request.approvedByName && (
                <Row label={request.status === 'REJECTED' ? 'Rejected By' : 'Approved By'} value={request.approvedByName} />
              )}
              {request.approvalDate && (
                <Row label="Decision Date" value={new Date(request.approvalDate).toLocaleDateString()} />
              )}
              {request.rejectionReason && <Row label="Reason" value={request.rejectionReason} />}
              {!request.approvedByName && (
                <Typography variant="body2" color="text.secondary">Awaiting approval.</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>Linked To</Typography>
              <Divider sx={{ mb: 1 }} />
              {request.contractNumber && <Row label="Contract" value={request.contractNumber} />}
              {request.contractorName && <Row label="Contractor" value={request.contractorName} />}
              {request.projectName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">Project</Typography>
                  <Typography
                    variant="body2" fontWeight="medium" color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => request.projectId && navigate(`/dashboard/projects/${request.projectId}`)}
                  >
                    {request.projectName}
                  </Typography>
                </Box>
              )}
              {request.farmOperationName && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="body2" color="text.secondary">Farm Operation</Typography>
                  <Typography
                    variant="body2" fontWeight="medium" color="primary"
                    sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                    onClick={() => request.farmOperationId && navigate(`/dashboard/farm/${request.farmOperationId}`)}
                  >
                    {request.farmOperationName}
                  </Typography>
                </Box>
              )}
              {request.operationTaskTitle && <Row label="Task" value={request.operationTaskTitle} />}
              {request.procurementItem && <Row label="Procurement Item" value={request.procurementItemCustom || request.procurementItem} />}
              {request.supplierName && <Row label="Supplier" value={request.supplierName} />}
              {!request.contractNumber && !request.contractorName && !request.projectName
                && !request.farmOperationName && !request.procurementItem && (
                <Typography variant="body2" color="text.secondary">Not linked to any record.</Typography>
              )}
              {request.paymentId && (
                <Box sx={{ mt: 2 }}>
                  <Button size="small" variant="outlined" onClick={() => navigate(`/dashboard/payments/${request.paymentId}`)}>
                    View Disbursed Payment
                  </Button>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{request.status === 'APPROVED' ? 'Cancel Request' : 'Reject Request'}</DialogTitle>
        <DialogContent>
          {request.status === 'APPROVED' && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This request is already approved but has not been disbursed. Cancelling it reverts it to Rejected.
            </Typography>
          )}
          <TextField
            label="Reason (optional)"
            multiline rows={2}
            fullWidth
            value={rejectReason}
            onChange={(e) => setRejectReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Back</Button>
          <Button
            variant="contained"
            color="error"
            disabled={rejectMutation.isPending}
            startIcon={rejectMutation.isPending ? <CircularProgress size={16} /> : <Cancel />}
            onClick={() => rejectMutation.mutate(rejectReason || 'Cancelled')}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
